import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  LabelText,
  MapWithControls,
  useInaccessibleElement,
} from '@sg/garnish';

import { LoadingAnimation } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { SearchArea } from './SearchArea';

export const MapView = (props: MapViewProps) => {
  const {
    showSearchAreaBtn,
    loading,
    onSearchAreaPress,
    onGeolocateUserBtnPress,
    ...mapProps
  } = props;
  const { t } = useLocalizationContext();

  // The map should be not be accessible to screen readers -
  // an invisible LabelText is provided instead.
  const mapAltText = t('location.map-a11y-alt-text');
  const mapA11yProps = useInaccessibleElement(true);

  const isEmptyRegion = mapProps.region.lat === 0 && mapProps.region.lng === 0;

  return (
    <>
      <LabelText
        testID="map-a11y-alt-text"
        style={styles.mapAltText}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel={mapAltText}
        accessibilityHint={mapAltText}
      >
        {mapAltText}
      </LabelText>

      <View testID="map-view" style={styles.mapViewContainer} {...mapA11yProps}>
        <SearchArea
          isLoading={loading}
          disabled={!showSearchAreaBtn}
          onPress={onSearchAreaPress}
        />

        {isEmptyRegion ? (
          <LoadingAnimation />
        ) : (
          <MapWithControls
            centerLocationAccessibilityLabel={t(
              'location.map-center-location-label',
            )}
            centerLocationAccessibilityHint={t(
              'location.map-center-location-hint',
            )}
            zoomInAccessibilityLabel={t('location.map-zoom-in-label')}
            zoomInAccessibilityHint={t('location.map-zoom-in-hint')}
            zoomOutAccessibilityLabel={t('location.map-zoom-out-label')}
            zoomOutAccessibilityHint={t('location.map-zoom-out-hint')}
            disableGeolocateUserBtn={loading}
            onGeolocateUserBtnPress={onGeolocateUserBtnPress}
            {...mapProps}
          />
        )}
      </View>
    </>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  mapViewContainer: {
    position: 'relative',
    flex: 1,
  },

  // hide map alt text label from users but leave it visible to screen readers
  mapAltText: {
    height: 1,
    width: 1,
    margin: -1,
    overflow: 'hidden',
    position: 'absolute',
  },
});

// ────────────────────────────────────────────────────────────────────────────────

type MapViewProps = {
  readonly loading?: boolean;
  readonly showSearchAreaBtn?: boolean;
  readonly onSearchAreaPress?: () => void;
} & React.ComponentProps<typeof MapWithControls>;
